import type { ReactNode } from 'react'
import { Pressable, StyleSheet } from 'react-native'

import { FigmaCheckIcon } from '../icons/FigmaSystemIcons'
import { Box, Text, useTheme } from '../theme/restyle'

export interface SelectableAssetRowProps {
  name: string
  /** The 36px leading avatar — a token, a chain, or a token-on-chain pair. */
  icon: ReactNode
  /** A second value the row carries, set against its trailing edge. */
  detail?: string
  selected: boolean
  /** Offered but not pickable, e.g. a chain that doesn't carry the picked token. */
  disabled?: boolean
  onPress: () => void
  accessibilityLabel: string
}

/** One row of a token or chain pick list (Figma `list-item/base`). */
export function SelectableAssetRow({
  name,
  icon,
  detail,
  selected,
  disabled = false,
  onPress,
  accessibilityLabel,
}: SelectableAssetRowProps) {
  const theme = useTheme()

  return (
    <Pressable
      onPress={onPress}
      disabled={disabled}
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel}
      accessibilityState={{ selected, disabled }}
    >
      <Box
        flexDirection="row"
        alignItems="center"
        borderRadius="element"
        style={[
          styles.row,
          selected ? { backgroundColor: theme.background.secondary } : null,
          disabled ? { opacity: theme.opacity.disabled / 100 } : null,
        ]}
      >
        {icon}
        <Box flex={1}>
          <Text
            variant="bodyMedium"
            numberOfLines={1}
            style={{ color: theme.content.elements.primary }}
          >
            {name}
          </Text>
        </Box>
        {detail === undefined ? null : (
          <Text
            variant="body"
            style={{ color: theme.content.elements.secondary }}
          >
            {detail}
          </Text>
        )}
        {selected ? (
          <FigmaCheckIcon size={20} color={theme.content.elements.secondary} />
        ) : null}
      </Box>
    </Pressable>
  )
}

const styles = StyleSheet.create({
  row: { gap: 12, paddingHorizontal: 12, paddingVertical: 10 },
})
